import React, { useState, useEffect } from 'react'
import * as D from 'date-fns'
import s from '../../styles.module.css'
import { useSurveyStore } from 'stores/survey-store'

const REGEX = /(0[1-9]|1[012])[- \/.](0[1-9]|[12][0-9]|3[01])[- \/.](19|20)\d\d/
const REGEX_INPUT = /(\d{0,2})\/?(\d{0,2})\/?(\d{0,4})/

const parseDate = (dateString: string): Date => new Date(dateString)
const stringifyDate = (date: Date): string => D.format(date, 'MM/DD/YYYY')

const parseDateParts = (dateString: string) => {
  const match = dateString.match(REGEX_INPUT)
  if (!match) return { month: undefined, day: undefined, year: undefined }
  const [, month, day, year] = match
  const formattedMonth = month ? Math.min(parseInt(month, 10), 12) : undefined
  const formattedDay = day ? Math.min(parseInt(day, 10), 31) : undefined
  return {
    month:
      formattedMonth !== undefined
        ? `${
            month.length === 2 && formattedMonth < 10 ? '0' : ''
          }${formattedMonth}`
        : undefined,
    day:
      formattedDay !== undefined
        ? `${day.length === 2 && formattedDay < 10 ? '0' : ''}${formattedDay}`
        : undefined,
    year,
  }
}

const getStringRepresentationOfDate = (dateString: string): string => {
  const { month, day, year } = parseDateParts(dateString)
  return (
    `${month || ''}${month && month.length === 2 ? '/' : ''}` +
    `${day || ''}${day && day.length === 2 ? '/' : ''}${year || ''}`
  )
}

const getError = (stringValue: string): string | null => {
  return REGEX.test(stringValue)
    ? null
    : 'Use the format MM/DD/YYYY. ex: 06/01/1986'
}

interface SurveyDateInputProps {
  value: Date | null
  onChange?: (date: Date | null) => void
  onBlur?: (date: Date | null) => void
  placeholder?: string
  questionId?: number | string
}

const SurveyDateInput: React.FC<SurveyDateInputProps> = ({
  value: initialValue,
  onChange: propOnChange,
  onBlur: propOnBlur,
  placeholder = '',
  questionId,
}) => {
  const { setError: setStoreError, errors } = useSurveyStore()
  const [value, setValue] = useState<Date | null>(initialValue || null)
  const [stringValue, setStringValue] = useState(
    value ? stringifyDate(value) : ''
  )

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue)
      setStringValue(stringifyDate(initialValue))
    } else {
      setValue(null)
      setStringValue('')
    }
  }, [initialValue])

  const handleBlur = () => {
    const validationError = getError(stringValue)
    if (validationError) {
      if (questionId) {
        setStoreError(questionId, validationError)
      }
      if (propOnBlur) {
        propOnBlur(null)
      }
      return
    }

    const newValue = parseDate(stringValue)
    setValue(newValue)
    if (questionId) {
      setStoreError(questionId, null)
    }
    if (propOnBlur) {
      propOnBlur(newValue)
    }
  }

  const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const newStringValue = ev.target.value

    if (
      newStringValue.length < stringValue.length &&
      (newStringValue.slice(-1) === '/' || stringValue.slice(-1) === '/')
    ) {
      setStringValue(newStringValue.substring(0, newStringValue.length - 1))
    } else {
      setStringValue(getStringRepresentationOfDate(newStringValue))
    }

    const validationError = getError(newStringValue)
    if (questionId) {
      setStoreError(questionId, validationError || null)
    }

    // If the date is valid, store it immediately
    if (!validationError && newStringValue.length === 10) {
      const newValue = parseDate(newStringValue)
      setValue(newValue)
      if (propOnChange) {
        propOnChange(newValue)
      }
    } else if (propOnChange) {
      // Clear the value if invalid
      propOnChange(null)
    }
  }

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight') {
      ev.stopPropagation()
    }
  }

  return (
    <div className={s.dateInputContainer}>
      <input
        className={s.dateInput}
        type="text"
        inputMode="numeric"
        value={stringValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ? `${placeholder} MM/DD/YYYY` : 'MM/DD/YYYY'}
        maxLength={10}
      />
    </div>
  )
}

export default SurveyDateInput
